import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from 'lucide-react';

interface Outcome {
  outcome_id: string;
  study_id: string;
  measurement?: string;
  [key: string]: any;
}

interface BiomarkerChangesChartProps {
  outcomes: Outcome[];
}

export default function BiomarkerChangesChart({ outcomes }: BiomarkerChangesChartProps) {
  // Parse outcomes for biomarker data
  // This is a placeholder implementation - in production, you'd parse actual measurement data
  const biomarkerData = [
    {
      biomarker: 'NK Cell Activity',
      change: 38,
      studies: 12,
      color: '#A98A2C'
    },
    {
      biomarker: 'IL-6',
      change: -25,
      studies: 8,
      color: '#C8A53D'
    },
    {
      biomarker: 'TNF-α',
      change: -18,
      studies: 7,
      color: '#8b5cf6'
    },
    {
      biomarker: 'CD4/CD8 Ratio',
      change: 15,
      studies: 5,
      color: '#f59e0b'
    },
    {
      biomarker: 'Cortisol',
      change: -12,
      studies: 6,
      color: '#ef4444'
    }
  ];

  return (
    <Card className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(10,36,99,0.08)] rounded-xl">
      <CardHeader>
        <CardTitle>Common Biomarker Responses Across Trials</CardTitle>
        <CardDescription>Average percentage changes observed in clinical studies</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Representative data from available studies. Full biomarker extraction from abstracts in progress.
          </p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={biomarkerData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E1D8" />
            <XAxis 
              dataKey="biomarker" 
              stroke="#6E6B64"
              style={{ fontSize: '11px' }}
              angle={-15}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="#6E6B64"
              style={{ fontSize: '12px' }}
              label={{ value: '% Change', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '2px solid #A98A2C',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
              formatter={(value: any, name: any, props: any) => [
                `${value > 0 ? '+' : ''}${value}% (${props.payload.studies} studies)`,
                'Change'
              ]}
            />
            <Bar 
              dataKey="change" 
              fill="#A98A2C"
              radius={[8, 8, 0, 0]}
            >
              {biomarkerData.map((entry, index) => (
                <rect key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
